import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Home as HomeIcon, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FeedbackWidget } from '@/components/feedback/FeedbackWidget';

type WorkspacePageLayoutProps = {
  title: string;
  icon?: React.ReactNode;
  onGoHome: () => void;
  onLogout: () => void;
  children: React.ReactNode;
};

export default function WorkspacePageLayout({
  title,
  icon,
  onGoHome,
  onLogout,
  children,
}: WorkspacePageLayoutProps) {
  const { t, i18n } = useTranslation();

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Background gradient */}
      <div
        className="absolute inset-0 pointer-events-none opacity-20"
        style={{
          background:
            'radial-gradient(circle at 18% 10%, hsl(var(--primary)) 0%, hsl(var(--muted)) 40%, hsl(var(--background)) 100%)',
        }}
      />

      <header className="relative z-10 flex items-center justify-between px-6 py-4 bg-background/90 backdrop-blur-sm border-b-2 border-border shadow-sm">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={onGoHome}>
            <HomeIcon className="h-4 w-4" />
            {t('home') || 'Accueil'}
          </Button>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
          <span className="font-semibold text-foreground flex items-center gap-2">
            {icon}
            {title}
          </span>
        </div>

        <div className="flex items-center gap-4">
          <FeedbackWidget />

          <Select value={i18n.language} onValueChange={(value) => i18n.changeLanguage(value)}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fr">Français</SelectItem>
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="ar">العربية</SelectItem>
            </SelectContent>
          </Select>

          <Button variant="brand" onClick={onLogout}>
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">{t('logout') || 'Déconnexion'}</span>
          </Button>
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto p-6">{children}</main>
    </div>
  );
}
